import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap, type CaseSummary } from "../api/client";
import { describeClass } from "../admin/RetentionSettingsPage";
import { utc } from "../import/items";
import { PlaceHold } from "../retention/RetentionPage";

/** A case's retention class and legal holds (threat model §2.2). */
export function CaseRetention({ c }: { c: CaseSummary }) {
  const qc = useQueryClient();
  const classes = useQuery({
    queryKey: ["retention", "classes"],
    queryFn: () => unwrap(api.GET("/api/retention/classes")),
  });
  const holds = useQuery({
    queryKey: ["holds"],
    queryFn: () => unwrap(api.GET("/api/holds")),
  });
  const [holding, setHolding] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["cases"] });
    void qc.invalidateQueries({ queryKey: ["retention"] });
    void qc.invalidateQueries({ queryKey: ["holds"] });
  };
  const setClass = useMutation({
    mutationFn: (classId: string | null) =>
      unwrap(
        api.PUT("/api/cases/{id}/retention", {
          params: { path: { id: c.id } },
          body: { class_id: classId },
        }),
      ),
    onSuccess: refresh,
  });
  const manages = c.permissions.includes("ManageCases");
  const mayHold = c.permissions.includes("LegalHold");
  const current = classes.data?.find((k) => k.id === c.retention_class);
  const onCase = holds.data?.filter(
    (h) => h.target_kind === "case" && h.target_id === c.id,
  );

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Retention"
    >
      <Typography variant="h6" component="h3" gutterBottom>
        Retention
      </Typography>
      <Stack
        direction="row"
        sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        {manages ? (
          <TextField
            size="small"
            select
            label="Retention class"
            value={c.retention_class ?? ""}
            onChange={(e) => setClass.mutate(e.target.value || null)}
            disabled={setClass.isPending}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">None: keep its recordings</MenuItem>
            {classes.data?.map((k) => (
              <MenuItem key={k.id} value={k.id}>
                {k.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Typography>
            {current
              ? current.name
              : "No retention class: its recordings are kept."}
          </Typography>
        )}
        {current && (
          <Typography variant="body2" color="text.secondary">
            {describeClass(current)}
            {current.basis === "case_closed" &&
              (c.closed_at
                ? `; closed ${utc(c.closed_at).slice(0, 10)}`
                : "; the case is open, so nothing is due yet")}
          </Typography>
        )}
        {onCase?.map((h) => (
          <Chip
            key={h.id}
            color="warning"
            label={`Legal hold: ${h.reason}`}
            sx={{ maxWidth: "100%" }}
          />
        ))}
        {mayHold && (
          <Button size="small" onClick={() => setHolding(true)}>
            Place legal hold
          </Button>
        )}
      </Stack>
      {setClass.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {setClass.error.message}
        </Alert>
      )}
      {holding && (
        <PlaceHold
          kind="case"
          id={c.id}
          name={c.name}
          onClose={() => setHolding(false)}
          onDone={() => {
            setHolding(false);
            refresh();
          }}
        />
      )}
    </Paper>
  );
}
